import {
  Button,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  closeCurrentDialog,
} from 'platform/components';
import {HStack, VStack} from 'platform/foundation';

import {isNil} from 'ramda';

import {useGetTenantQuery, usePutTireOrderAuthorizationProfileMutation} from '@dms/api/core';
import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {
  tireOrderAuthorizationProfileSchema,
  type TireOrderAuthorizationProfileFormValues,
} from './tireOrderAuthorizationProfileSchema';

export interface TireOrderAuthorizationProfileDialogProps {
  authorizationProfileId?: string | null;
  branchId?: string;
  orderId: string;
}

export function TireOrderAuthorizationProfileDialog(
  props: TireOrderAuthorizationProfileDialogProps
) {
  const {data: tenant} = useGetTenantQuery();
  const [putTireOrderAuthorizationProfile] = usePutTireOrderAuthorizationProfileMutation();

  const {data: authorizationProfiles, isLoading: isLoadingAuthorizationProfiles} =
    useGetAuthorizationProfilesQuery(
      {
        'x-tenant': tenant?.id ?? '',
        branchId: props.branchId,
      },
      {
        skip: isNil(props.branchId) || isNil(tenant?.id),
      }
    );

  const authorizationProfileOptions =
    authorizationProfiles
      ?.filter((profile) => profile.isActive)
      .map((profile) => ({
        label: profile.name,
        value: profile.id,
      })) ?? [];

  const handleSubmit: FormSubmitHandler<TireOrderAuthorizationProfileFormValues> = (values) =>
    putTireOrderAuthorizationProfile({
      orderId: props.orderId,
      authorizationProfileId: values.authorizationProfileId,
    })
      .unwrap()
      .then(closeCurrentDialog)
      .catch(handleApiError);

  const defaultValues: Partial<TireOrderAuthorizationProfileFormValues> = {
    authorizationProfileId: props.authorizationProfileId ?? undefined,
  };

  return (
    <Form<TireOrderAuthorizationProfileFormValues>
      experimentalZodSchema={tireOrderAuthorizationProfileSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
    >
      {(control) => (
        <VStack spacing={4}>
          <FormField
            type="choice"
            name="authorizationProfileId"
            control={control}
            options={authorizationProfileOptions}
            isLoading={isLoadingAuthorizationProfiles}
            label={i18n.t('entity.header.labels.authorizationProfile')}
            menuInPortal
          />
          <HStack justify="flex-end" spacing={2}>
            <Button
              variant="secondary"
              onClick={closeCurrentDialog}
              title={i18n.t('general.actions.discard')}
            />
            <FormButton
              control={control}
              type="submit"
              variant="primary"
              title={i18n.t('general.actions.save')}
            />
          </HStack>
        </VStack>
      )}
    </Form>
  );
}
